import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BusinessIcon from '@mui/icons-material/Business'
import ContactsIcon from '@mui/icons-material/Contacts'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FolderIcon from '@mui/icons-material/Folder'
import CloudIcon from '@mui/icons-material/Cloud'
import GroupIcon from '@mui/icons-material/Group'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PeopleIcon from '@mui/icons-material/People'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import { CONTENT_MAX_WIDTH, SIDEBAR_WIDTH } from '../../theme'
import { NotificationBell } from './NotificationBell'

type NavItem = { to: string; label: string; icon: React.ReactNode }

const MAIN_NAV: NavItem[] = [
  { to: '/', label: 'היום שלי', icon: <DashboardIcon fontSize="small" /> },
  { to: '/tasks', label: 'משימות', icon: <AssignmentIcon fontSize="small" /> },
  { to: '/cases', label: 'תיקים', icon: <FolderIcon fontSize="small" /> },
  { to: '/clients', label: 'לקוחות', icon: <PeopleIcon fontSize="small" /> },
  { to: '/projects', label: 'פרויקטים', icon: <BusinessIcon fontSize="small" /> },
  { to: '/contacts', label: 'אנשי קשר', icon: <ContactsIcon fontSize="small" /> },
  { to: '/templates', label: 'תבניות משימות', icon: <PlaylistAddCheckIcon fontSize="small" /> },
]

const ADMIN_NAV: NavItem[] = [
  { to: '/settings/users', label: 'ניהול משתמשים', icon: <GroupIcon fontSize="small" /> },
  { to: '/settings/drive', label: 'חיבור Google Drive', icon: <CloudIcon fontSize="small" /> },
  { to: '/settings/tokens', label: 'טוקני API', icon: <VpnKeyIcon fontSize="small" /> },
]

type ExternalLink = { href: string; label: string; caption: string; icon: React.ReactNode }

const EXTERNAL_LINKS: ExternalLink[] = [
  {
    href: 'https://secapp.taxes.gov.il/svnewmyazeg/frmKnisa.aspx?',
    label: 'מערכת מייצגים',
    caption: 'רשות המיסים',
    icon: <AccountBalanceIcon fontSize="small" />,
  },
]

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        px: 2.5,
        pt: 2,
        pb: 0.5,
        display: 'block',
        color: 'text.disabled',
        fontWeight: 600,
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </Typography>
  )
}

export function AppShell() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to)

  const go = (to: string) => {
    navigate(to)
    setMobileOpen(false)
  }

  const renderItems = (items: NavItem[]) =>
    items.map((item) => (
      <ListItemButton key={item.to} selected={isActive(item.to)} onClick={() => go(item.to)}>
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    ))

  const navigation = (
    <Box role="navigation" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box component="img" src="/logo.svg" alt="" sx={{ width: 32, height: 32 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
              ניהול משרד
            </Typography>
            <Typography variant="caption" color="text.secondary">
              חוזי מקרקעין
            </Typography>
          </Box>
        </Stack>
      </Toolbar>
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', pt: 1 }}>
        <List disablePadding>{renderItems(MAIN_NAV)}</List>

        <SectionLabel>קישורים חיצוניים</SectionLabel>
        <List disablePadding>
          {EXTERNAL_LINKS.map((link) => (
            <ListItemButton
              key={link.href}
              component="a"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
            >
              <ListItemIcon>{link.icon}</ListItemIcon>
              <ListItemText primary={link.label} secondary={link.caption} />
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </ListItemButton>
          ))}
        </List>

        {user?.role === 'ADMIN' ? (
          <>
            <SectionLabel>הגדרות</SectionLabel>
            <List disablePadding>{renderItems(ADMIN_NAV)}</List>
          </>
        ) : null}
      </Box>

      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1, pb: 1 }}>
          <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: '0.8125rem' }}>
            {user?.name?.trim().charAt(0) ?? ''}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user?.role === 'ADMIN' ? 'מנהל' : 'עובד'}
            </Typography>
          </Box>
        </Stack>
        <ListItemButton onClick={() => void logout()} sx={{ mx: 0 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="התנתקות" />
        </ListItemButton>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* MUI swaps the left/right anchors under an RTL theme, so "left" lands on the
          right — which is also the side the flex row reserves for the docked drawer. */}
      <Drawer
        anchor="left"
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: { md: SIDEBAR_WIDTH },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#fbfcfd',
            borderColor: 'divider',
          },
        }}
      >
        {navigation}
      </Drawer>

      {/* The bar is sticky inside the content column, so no width or margin maths. */}
      <Box
        component="main"
        sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, md: 60 } }}>
            {!isDesktop ? (
              <IconButton edge="start" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <NotificationBell />
            {isDesktop ? (
              <Tooltip title={user?.email ?? ''}>
                <Typography variant="body2" sx={{ ml: 1.5 }} color="text.secondary">
                  {user?.name}
                </Typography>
              </Tooltip>
            ) : null}
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, pt: 3, pb: 6 }}>
          <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
